import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

// Models
import { ZonaViewModel } from "src/app/_core/models/centros/zona.model";

// Services
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";

@Component({
    selector: "app-zona-autocomplete",
    templateUrl: "./zona-autocomplete.component.html",
    styleUrls: ["./zona-autocomplete.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ZonaAutocompleteComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class ZonaAutocompleteComponent
	implements OnInit, OnDestroy, ControlValueAccessor
{
	private readonly destroy$ = new Subject<void>();

	// Inputs
	@Input() label = "Zona";
	@Input() placeholder = "Buscar zona por descripción";
	@Input() required = false;
	@Input() hint = "Primero seleccione sociedad y responsable";

	// Inputs para dependencias
	@Input() set sociedadID(value: number | null) {
		this._sociedadID = value;
		this.checkDependenciesAndLoad();
	}
	get sociedadID(): number | null {
		return this._sociedadID;
	}

	@Input() set responsableID(value: number | null) {
		const valorAnterior = this._responsableID;
		this._responsableID = value;
		// Si el responsable ya tenía valor y ha cambiado, limpiar la zona seleccionada
		if (valorAnterior !== null && valorAnterior !== value) {
			this.clearSelection();
		}
		this.checkDependenciesAndLoad();
	}
	get responsableID(): number | null {
		return this._responsableID;
	}

	// Outputs
	@Output() zonaSelected = new EventEmitter<ZonaViewModel>();

	// State
	private _sociedadID: number | null = null;
	private _responsableID: number | null = null;
	zonas: ZonaViewModel[] = [];
	zonaId: number | null = null;
	selectedZona: ZonaViewModel | null = null;
	isDisabledByDependencies = true;
	noZonasAvailable = false;
	isLoadingZonas = false;

	// ControlValueAccessor
	private onChange = (_value: number | null): void => {
		// This is intentional
	};
	private onTouched = (): void => {
		// This is intentional
	};

	constructor(
		private readonly masterService: MasterService,
		private readonly notificationService: NotificationService,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		// La carga se dispara cuando llegan las dependencias.
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ControlValueAccessor implementation
	writeValue(value: number | null): void {
		this.zonaId = value ?? null;
		this.syncSelected();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: number | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(_isDisabled: boolean): void {
		// No hacer nada aquí, el disabled lo controla checkDependenciesAndLoad
	}

	/**
	 * Hint dinámico según el estado de dependencias / carga.
	 */
	get currentHint(): string {
		if (this.noZonasAvailable) {
			return "⚠️ No hay zonas disponibles para esta combinación de sociedad y responsable";
		}
		if (this.isDisabledByDependencies) {
			return this.hint;
		}
		if (this.isLoadingZonas) {
			return "";
		}
		return "Busque por descripción de zona";
	}

	/**
	 * Verifica las dependencias y carga las zonas si están disponibles
	 */
	private checkDependenciesAndLoad(): void {
		// Si faltan sociedadID o responsableID, deshabilitar y limpiar
		if (!this._sociedadID || !this._responsableID) {
			this.isDisabledByDependencies = true;
			this.zonas = [];
			this.noZonasAvailable = false;
			this.clearSelection();
			this.cdr.markForCheck();
			return;
		}

		// Si ambos están disponibles, habilitar y cargar zonas
		this.isDisabledByDependencies = false;
		this.noZonasAvailable = false;
		this.loadZonas();
	}

	/**
	 * Carga la lista de zonas según sociedad y responsable
	 */
	private loadZonas(): void {
		if (!this._sociedadID || !this._responsableID) {
			return;
		}

		this.isLoadingZonas = true;
		this.masterService
			.getZonasUnidadesOrganicas(this._sociedadID, this._responsableID)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (zonas) => {
					this.zonas = zonas;
					this.isLoadingZonas = false;

					// Verificar si la lista está vacía
					if (!zonas || zonas.length === 0) {
						this.noZonasAvailable = true;
						this.isDisabledByDependencies = true; // Deshabilitar si no hay zonas
						this.clearSelection();
					} else {
						this.noZonasAvailable = false;
						this.isDisabledByDependencies = false; // Habilitar solo si hay zonas
						this.syncSelected();
					}

					this.cdr.markForCheck();
				},
				error: () => {
					this.isLoadingZonas = false;
					this.noZonasAvailable = false;
					this.notificationService.showError(
						"Error al cargar las zonas",
					);
					this.cdr.markForCheck();
				},
			});
	}

	/** Recalcula el objeto seleccionado a partir del id actual. */
	private syncSelected(): void {
		this.selectedZona =
			this.zonaId != null
				? (this.zonas.find((z) => z.id === this.zonaId) ?? null)
				: null;
	}

	/**
	 * Obtiene el texto mostrado para una zona
	 */
	displayZona = (zona: ZonaViewModel | string): string => {
		if (typeof zona === "string") {
			return zona;
		}
		return zona && zona.descripcion ? zona.descripcion : "";
	};

	/** Selección desde el autocomplete `.ds`. */
	onValueChange(zona: ZonaViewModel | null): void {
		this.zonaId = zona ? zona.id : null;
		this.selectedZona = zona;
		this.onChange(this.zonaId);
		this.onTouched();
		if (zona) {
			this.zonaSelected.emit(zona);
		}
		this.cdr.markForCheck();
	}

	onControlTouched(): void {
		this.onTouched();
	}

	/**
	 * Limpia la selección y establece el valor a null
	 */
	private clearSelection(): void {
		this.zonaId = null;
		this.selectedZona = null;
		this.onChange(null);
		this.onTouched();
		this.cdr.markForCheck();
	}
}
